import {
  Smartphone,
  QrCode,
  Users,
  Download,
  Globe,
  Handshake,
  LineChart,
  Target,
  Wifi,
  BarChart3,
  BookOpen,
  Mic,
  Palette,
  Rocket,
  Building2,
  Settings,
  DollarSign,
  Shield,
  Languages,
  Brain,
  Heart,
  Sparkles,
  MessageCircle,
  CalendarCheck,
  Star,
  ClipboardList,
  Link,
  Zap,
  SmartphoneNfc,
} from 'lucide-react';
import { NewsDetailData } from './types';

// ━━━ Share 正式リリース (ID: 7) ━━━
export const shareData: NewsDetailData = {
  slug: 'share-release',
  badgeIcon: Smartphone,
  badgeText: 'Press Release',
  title: 'デジタル名刺サービス「Share」正式リリース',
  date: '2025年6月1日',
  breadcrumbLabel: 'デジタル名刺サービス「Share」正式リリース',
  lead: '株式会社Senriganは、複数のSNSアカウントを一元管理できる次世代デジタル名刺サービス「Share」を2025年6月1日に正式リリースしました。',
  overviewParagraphs: [
    '「Share」は、複数のSNSアカウントと連絡先情報を一つのデジタルプロフィールにまとめ、QRコードやNFCを通じて簡単に共有できるプラットフォームです。名刺交換のデジタル版として、ビジネスやプライベートでのコミュニケーションをスムーズにします。',
    '従来の名刺交換では、SNSアカウントを一つひとつ教える必要がありましたが、「Share」を使用することで、QRコードを見せるだけですべてのSNSアカウントを一度に共有できるようになります。これにより、名刺交換後のフォローアップが格段に効率化されます。',
  ],
  features: [
    {
      icon: Users,
      title: '複数SNSの一元管理',
      description: 'LINE、YouTube、X、Instagramなど10種類以上のSNSアカウントを一つのプロフィールページにまとめて管理できます。',
    },
    {
      icon: QrCode,
      title: 'QRコード共有',
      description: 'ワンクリックでQRコードを生成。スマートフォンで読み取るだけで、すべてのSNSアカウントに瞬時にアクセス可能です。',
    },
    {
      icon: Download,
      title: 'ワンタップ連絡先登録',
      description: 'プロフィール情報をワンタップでスマホの連絡先アプリに登録できます。名前、電話番号、メールアドレスなどの全ての情報が瞬時に保存されます。',
    },
    {
      icon: Globe,
      title: 'マルチデバイス対応',
      description: 'iOSとAndroid両方に最適化されたモバイルフレンドリーなデザインで、どのデバイスからでも快適に利用できます。',
    },
  ],
  useCases: [
    '名刺交換でのSNSアカウント共有',
    'ビジネスイベントでの連絡先交換',
    'マーケティング活動でのフォロワー獲得',
    'チーム内での連絡先統一管理',
  ],
  pricing: {
    plans: [
      { title: '月額プラン', price: '¥500', note: '税込・月額' },
      { title: '年額プラン', price: '¥5,000', note: '税込・年額（2ヶ月分お得）', highlight: true },
    ],
    footer: '7日間無料トライアル実施中',
  },
  roadmap: '今後はより多くのSNSプラットフォームへの対応、NFC機能の強化、AI自己紹介文生成機能の追加などを予定しています。また、既存の法人向けプランにおいても、企業のブランディング統一機能やチーム管理機能のさらなる拡充により、ビジネスシーンでの活用をより促進してまいります。',
  quote: {
    text: '現代のビジネスシーンにおいて、SNSでのつながりはますます重要になっています。『Share』は、この複雑化したデジタルコミュニケーションをシンプルで効率的なものに変える革新的なソリューションです。多くのビジネスパーソンに愛用していただき、より豊かなコミュニケーションの実現に貢献したいと考えています。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: '「Share」を今すぐ体験',
    subtext: '7日間無料トライアルで、すべての機能をお試しいただけます',
    primaryButton: { label: 'サービスサイトを見る', url: 'https://sns-share.com' },
    secondaryButton: { label: '無料で始める', url: 'https://app.sns-share.com/auth/signup' },
  },
};

// ━━━ ゴルフ上達アプリ「上手くなる気がするぅぅぅ」(ID: 8) ━━━
export const kigasuruData: NewsDetailData = {
  slug: 'kigasuru-release',
  badgeIcon: LineChart,
  badgeText: 'Press Release',
  title: 'ゴルフ上達アプリ「上手くなる気がするぅぅぅ」正式リリース',
  date: '2025年10月25日',
  breadcrumbLabel: 'ゴルフ上達アプリ「上手くなる気がするぅぅぅ」正式リリース',
  lead: '株式会社Senriganは、スマートフォンのジャイロセンサーを活用してゴルフのショット精度を可視化・分析するアプリ「上手くなる気がするぅぅぅ」を2025年10月25日に正式リリースしました。',
  overviewParagraphs: [
    '本アプリは、スマートフォンのジャイロセンサーで傾斜を自動検出しながらショットを記録し、クラブ別・条件別のパフォーマンスを分析するゴルフ上達支援ツールです。感覚に頼りがちな練習を、データに基づいた効率的な上達へと変えます。',
    '「なんとなく打てた」「なんとなくダメだった」を卒業し、ヒートマップやグラフで自分の本当の課題を発見できます。PWA対応で、ラウンド中の電波が届かない場所でもオフラインで記録可能です。',
  ],
  features: [
    {
      icon: Target,
      title: 'タップで簡単ショット記録',
      description: 'グリッド上をタップするだけでショットの着弾地点を記録。ジャイロセンサーが傾斜やライの角度を自動で計測し、手入力の手間を省きます。',
    },
    {
      icon: BarChart3,
      title: 'クラブ別パフォーマンス分析',
      description: 'ドライバーからパターまで、クラブごとの精度をヒートマップとグラフで可視化。最も改善が必要なクラブが一目で分かります。',
    },
    {
      icon: LineChart,
      title: '条件別の傾向分析',
      description: '傾斜、風速、気温などの条件ごとにショット精度の傾向を分析。苦手な状況を特定し、効率的な練習計画を立てられます。',
    },
    {
      icon: Wifi,
      title: 'オフライン対応',
      description: 'PWA技術でコース上の電波が不安定な場所でもデータ記録が可能。オンライン復帰時に自動で同期します。',
    },
  ],
  useCases: [
    'ラウンド中のリアルタイムショット記録',
    '練習場での反復練習の精度トラッキング',
    'レッスンプロへの相談時のデータ共有',
    'ゴルフ仲間とのスコア改善の比較',
  ],
  pricing: {
    plans: [
      { title: '月額プラン', price: '¥980', note: '税込・月額' },
      { title: '年額プラン', price: '¥9,800', note: '税込・年額（2ヶ月分お得）', highlight: true },
      { title: '買い切りプラン', price: '¥29,800', note: '税込・永久利用' },
    ],
    footer: '無料トライアル期間あり',
  },
  roadmap: 'AIによるスイング改善アドバイス機能、GPSを活用したコースマップとの連動、ゴルフ仲間とのソーシャル機能など、ゴルファーの上達をさらに加速させる機能の開発を進めてまいります。',
  quote: {
    text: 'ゴルフの上達には「感覚」だけでなく「データ」が必要です。このアプリは、アマチュアゴルファーが自分のプレーを客観的に見つめ直し、本当に改善すべきポイントを発見するための相棒になることを目指しました。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: '「上手くなる気がするぅぅぅ」を体験',
    subtext: 'まずは無料トライアルで、データに基づくゴルフ上達を体感してください',
    primaryButton: { label: 'サービスサイトを見る', url: 'https://kigasuru.com/' },
  },
};

// ━━━ 電子書籍出版代行「著力」(ID: 9) ━━━
export const choryokuData: NewsDetailData = {
  slug: 'choryoku-release',
  badgeIcon: BookOpen,
  badgeText: 'Press Release',
  title: '電子書籍出版代行サービス「著力（ちょりょく）」リリース',
  date: '2026年1月31日',
  breadcrumbLabel: '電子書籍出版代行サービス「著力」リリース',
  lead: '株式会社Senriganは、経営者・専門家向けの電子書籍出版代行サービス「著力（ちょりょく）」を2026年1月31日にリリースしました。取材から出版までワンストップで対応します。',
  overviewParagraphs: [
    '「著力」は、忙しい経営者や専門家の方が、自身の知識・経験を一冊の電子書籍としてKindleに出版できる代行サービスです。オンライン取材で話すだけで原稿が完成し、表紙デザイン、校正、Kindle出版手続きまでをすべて代行します。',
    '「本を出したいけど時間がない」「文章を書くのが苦手」という方でも、プロのライターがあなたの言葉を丁寧に一冊にまとめます。ブランディング、集客、採用活動など、電子書籍がビジネスの武器になります。',
  ],
  features: [
    {
      icon: Mic,
      title: 'オンライン取材で完結',
      description: '1〜2回のオンライン取材（各1〜2時間）であなたの話を聞くだけ。文章を書く必要は一切ありません。',
    },
    {
      icon: Palette,
      title: 'プロの表紙デザイン',
      description: '読者の目を引く表紙をプロのデザイナーが制作。ジャンルやブランドイメージに合わせたデザインをご提案します。',
    },
    {
      icon: Rocket,
      title: '最短2週間で出版',
      description: '企画から出版まで最短2週間のスピード対応。ビジネスチャンスを逃さないタイムリーな出版が可能です。',
    },
    {
      icon: BookOpen,
      title: 'Kindle出版手続き代行',
      description: 'KDP（Kindle Direct Publishing）のアカウント設定から書籍登録、カテゴリ設定まで、出版に必要な手続きをすべて代行します。',
    },
  ],
  useCases: [
    '経営者の知見・哲学を一冊にまとめてブランディングに活用',
    '士業・コンサルタントの専門知識を書籍化して集客に活用',
    '採用活動で自社の理念やカルチャーを伝える社長本',
    'セミナー・講演の内容を書籍化して販売・配布',
  ],
  pricing: {
    plans: [
      { title: 'ライトプラン', price: '¥55,000〜', note: '税込・短編（1〜2万字）' },
      { title: 'スタンダードプラン', price: '¥110,000〜', note: '税込・標準（3〜5万字）', highlight: true },
    ],
    footer: '初回相談無料・お見積り後のキャンセルも可能です',
  },
  roadmap: '今後はオーディオブック対応、Amazon以外のプラットフォーム（楽天Kobo等）への同時出版、出版後のマーケティング支援サービスの提供を予定しています。',
  quote: {
    text: '経営者の頭の中にある知識や経験は、本という形にすることで、何十倍もの価値を生み出します。「著力」は、忙しい経営者の方でも気軽に出版できる仕組みを作りました。話すだけで一冊が生まれる体験を、ぜひお試しください。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: '「著力」で電子書籍を出版する',
    subtext: '初回相談は無料です。まずはお気軽にお問い合わせください',
    primaryButton: { label: 'サービスサイトを見る', url: 'https://bookpower.jp/' },
  },
};

// ━━━ Share ホワイトラベル (ID: 10) ━━━
export const shareWhitelabelData: NewsDetailData = {
  slug: 'share-whitelabel',
  badgeIcon: Handshake,
  badgeText: 'Press Release',
  title: 'デジタル名刺「Share」ホワイトラベルパートナー募集開始',
  date: '2026年3月27日',
  breadcrumbLabel: 'Share ホワイトラベルパートナー募集',
  lead: '株式会社Senriganは、デジタル名刺サービス「Share」を自社ブランドで展開できるホワイトラベルパートナープログラムの募集を2026年3月27日に開始しました。',
  overviewParagraphs: [
    '本プログラムは、印刷業・広告代理店・Web制作会社などが、開発費ゼロ・在庫リスクゼロでデジタル名刺サービスを自社ブランドとして立ち上げられるホワイトラベルプランです。独自ドメイン、独自ロゴ、独自カラーで完全に自社サービスとして展開できます。',
    '紙の名刺市場が縮小する中、デジタル名刺は新たな収益源として注目されています。既存の顧客基盤を活かし、月額サブスクリプションによる継続収益モデルを構築できます。最短30日でサービスを開始可能です。',
  ],
  features: [
    {
      icon: Building2,
      title: '完全自社ブランド化',
      description: '独自ドメイン、ロゴ、カラー、メールアドレスまで、すべてを自社ブランドに統一。「Share」の名前は一切表示されません。',
    },
    {
      icon: Settings,
      title: '専用テナント管理',
      description: 'パートナー専用の管理画面から、法人顧客の作成・管理、ユーザー招待、プラン設定などを一元管理できます。',
    },
    {
      icon: DollarSign,
      title: '継続収益モデル',
      description: '月額サブスクリプション型のビジネスモデル。紙の名刺の一回きりの売上から、安定した月額収益への転換を実現します。',
    },
    {
      icon: Shield,
      title: '開発・運用リスクゼロ',
      description: 'システム開発費、サーバー構築費、保守運用費はすべて不要。技術的な知識がなくても、すぐにサービスを開始できます。',
    },
  ],
  useCases: [
    '名刺印刷会社がデジタル名刺をラインナップに追加',
    'Web制作会社が顧客に付加価値サービスとして提供',
    '広告代理店の新規事業としてデジタル名刺事業を立ち上げ',
    'IT企業が自社プロダクトの一つとして展開',
  ],
  roadmap: '今後はパートナー向けの販売支援ツール、共同マーケティングプログラム、DX補助金申請サポートなど、パートナー企業のビジネス拡大を支援するプログラムをさらに充実させてまいります。',
  quote: {
    text: '印刷業界をはじめ、多くの企業がデジタルシフトの波に直面しています。このホワイトラベルプログラムは、既存の顧客基盤と信頼関係を活かしながら、新しい収益源を確保するための最短ルートです。パートナー企業の皆様と共に、名刺のデジタル化を推進してまいります。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: 'パートナープログラムの詳細を見る',
    subtext: '初期費用・在庫リスクゼロ。まずは資料をご確認ください',
    primaryButton: { label: 'パートナーページを見る', url: 'https://sns-share.com/partner' },
  },
};

// ━━━ Your Kanji Name (ID: 11) ━━━
export const kanjiNameData: NewsDetailData = {
  slug: 'kanjiname-release',
  badgeIcon: Languages,
  badgeText: 'Press Release',
  title: '漢字名命名アプリ「Your Kanji Name」リリース',
  date: '2026年3月15日',
  breadcrumbLabel: '漢字名命名アプリ「Your Kanji Name」リリース',
  lead: '株式会社Senriganは、AIが性格診断に基づいて外国人に最もふさわしい漢字名を授けるWebアプリ「Your Kanji Name」を2026年3月15日にリリースしました。',
  overviewParagraphs: [
    '「Your Kanji Name」は、16問の性格診断を通じてユーザーの人柄や価値観を分析し、その人にふさわしい意味を持つ漢字名を生成するWebアプリです。音の当て字で無理やり漢字にする従来サービスとは異なり、漢字本来の意味からアプローチする新しい手法を採用しています。',
    '10言語（日本語除く）に対応し、アクセスする端末の言語を自動判定して最適な言語で表示。従来の「自分で言語を選ぶ」常識を覆す、ストレスフリーな多言語体験を実現しています。性別や性格特性を考慮した精度の高い漢字マッチングで、世界中のユーザーが自分だけの漢字名を手に入れることができます。',
  ],
  features: [
    {
      icon: Brain,
      title: 'AI性格診断（16問）',
      description: '6つの根源的な動機（知識、創造、帰属、支配、安定、自由）を16問の質問で分析。あなたの内面にマッチした漢字を選定します。',
    },
    {
      icon: Languages,
      title: '意味ベースの漢字選定',
      description: '音の当て字ではなく、漢字の本来の意味から名前を構成。受け取った漢字名の一文字一文字に込められた意味を理解できます。',
    },
    {
      icon: Sparkles,
      title: '性別・性格を考慮した精度',
      description: '宣言された性別と性格特性の両方を-10〜+10のスケールで評価し、その人らしさを反映した自然な漢字名を生成します。',
    },
    {
      icon: Globe,
      title: '10言語自動切替',
      description: 'アクセスする端末の言語を自動判定し、最適な言語で表示。「言語を選ぶ」操作が不要な、従来の常識を覆すストレスフリーな多言語対応です。',
    },
  ],
  useCases: [
    '訪日外国人の観光体験・お土産として',
    '日本文化に興味がある海外ユーザーの自己発見ツール',
    'インバウンド向け店舗・宿泊施設でのおもてなし体験',
    'SNSでのシェアを通じた日本文化の発信',
  ],
  roadmap: '今後は漢字名の書道風デジタル証明書の発行、漢字の書き順アニメーション機能、より多くの言語への対応を予定しています。また、法人向けにインバウンド集客ツールとしての提供も計画しています。',
  quote: {
    text: '外国人の方に漢字名を贈る文化は古くからありますが、多くは音だけを無理やり漢字に当てはめたものでした。「Your Kanji Name」は、その人の性格や価値観を理解した上で、本当にふさわしい意味の漢字を選びます。世界中の方に、漢字の奥深い魅力を体験していただきたいです。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: 'あなただけの漢字名を見つける',
    subtext: '16問の質問に答えるだけ。約3分で、あなたにぴったりの漢字名が見つかります',
    primaryButton: { label: 'サイトを見てみる', url: 'https://app.kanjiname.jp' },
  },
};

// ━━━ RINEちゃん (ID: 12) ━━━
export const rinechanData: NewsDetailData = {
  slug: 'rinechan-release',
  badgeIcon: Heart,
  badgeText: 'Press Release',
  title: '顧客管理アプリ「RINEちゃん」リリース',
  date: '2026年2月10日',
  breadcrumbLabel: '顧客管理アプリ「RINEちゃん」リリース',
  lead: '株式会社Senriganは、キャバクラ・ガールズバーで働く女性のための専用顧客管理アプリ「RINEちゃん」を2026年2月10日にリリースしました。',
  overviewParagraphs: [
    '「RINEちゃん」は、ナイトワークで働く女性が、お客様との関係性を効率的に管理するための専用CRMアプリです。LINE連絡の記録、来店履歴・売上管理、リマインダー機能を搭載し、お客様への対応品質の向上と売上アップを支援します。',
    '紙のノートやスマホのメモアプリで管理していたお客様情報を一元化。「このお客様、前回いつ来たっけ？」「誕生日はいつだっけ？」といった情報をいつでも確認できます。お客様一人ひとりに寄り添った接客で、指名・リピート率のアップにつなげます。',
  ],
  features: [
    {
      icon: Users,
      title: '3段階名前管理',
      description: 'LINE名・ニックネーム・本名の3段階で顧客を管理。呼び方を間違える心配がなくなり、お客様に「覚えてくれている」安心感を与えます。',
    },
    {
      icon: MessageCircle,
      title: 'LINE会話記録',
      description: 'お客様とのLINEやり取りの要点を記録。過去の会話内容を振り返ることで、次回の会話のきっかけづくりに活用できます。',
    },
    {
      icon: ClipboardList,
      title: '来店・売上管理',
      description: '来店日時、滞在時間、同伴者、売上をお客様ごとに記録。VIPのお客様や最近来店が減ったお客様を一目で把握できます。',
    },
    {
      icon: CalendarCheck,
      title: 'リマインダー機能',
      description: '「3日後にLINEを送る」「誕生日の前日に連絡する」など、お客様へのアクションを忘れないようリマインダーを設定できます。',
    },
  ],
  useCases: [
    'お客様の誕生日や記念日を忘れずにお祝い',
    '来店が減ったお客様への適切なタイミングでの連絡',
    '同伴・指名の実績を振り返って接客を改善',
    'ブラックリスト管理でトラブル客を事前に把握',
  ],
  pricing: {
    plans: [
      { title: 'Basicプラン', price: '¥1,078', note: '税込・月額（100名まで）' },
      { title: 'Proプラン', price: '¥3,278', note: '税込・月額（1,000名まで）', highlight: true },
    ],
    footer: '7日間無料トライアル・年額プランは割引あり',
  },
  roadmap: '今後はAIによる最適な連絡タイミングの提案、売上分析ダッシュボード、他キャストとの顧客共有機能（店舗プラン）などの開発を予定しています。ナイトワークで頑張る女性の働き方をテクノロジーで支援してまいります。',
  quote: {
    text: 'ナイトワークで成功するためには、お客様一人ひとりとの信頼関係が何より大切です。「RINEちゃん」は、その信頼関係を築くための記憶と行動をサポートするパートナーアプリです。忙しい毎日の中でも、大切なお客様を忘れない仕組みを提供します。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: '「RINEちゃん」を無料で試す',
    subtext: '7日間無料トライアルですべての機能をお試しいただけます',
    primaryButton: { label: 'サービスサイトを見る', url: 'https://linechan.online/' },
  },
};

// ━━━ 日程調整アプリ「アワセル」(ID: 14) ━━━
export const awaseruData: NewsDetailData = {
  slug: 'awaseru-release',
  badgeIcon: CalendarCheck,
  badgeText: 'Press Release',
  title: '日程調整アプリ「アワセル」リリース',
  date: '2025年5月15日',
  breadcrumbLabel: '日程調整アプリ「アワセル」リリース',
  lead: '株式会社Senriganは、登録不要・完全無料の日程調整アプリ「アワセル」を2025年5月15日にリリースしました。URLを共有するだけで誰とでも日程調整ができます。',
  overviewParagraphs: [
    '「アワセル」は、LINEを交換していない相手ともURLを共有するだけで日程調整ができるWebアプリです。既存の日程調整サービスにありがちな広告表示やごちゃついたUIを排除し、シンプルで快適な操作性を追求しました。',
    '参加者の名前を入力し、候補日をカレンダーから選ぶだけの3ステップで調整が完了。回答はリアルタイムに反映され、全員の都合が一目で分かります。会員登録もアプリのインストールも一切不要です。',
  ],
  features: [
    {
      icon: Zap,
      title: '登録不要・完全無料',
      description: '会員登録もアプリインストールも不要。URLを開くだけですぐに使い始められます。広告も一切表示されません。',
    },
    {
      icon: Link,
      title: 'URL共有で誰とでも',
      description: 'LINEの友だちでなくても、メールやSNSなどあらゆる手段でURLを共有するだけ。相手を選ばない日程調整を実現します。',
    },
    {
      icon: Users,
      title: 'リアルタイム回答反映',
      description: '参加者の回答はリアルタイムに反映。全員の都合を一覧で確認でき、最適な日程がすぐに分かります。',
    },
    {
      icon: CalendarCheck,
      title: 'カレンダー追加（2種類）',
      description: '決定した日程をGoogleカレンダーまたはiCalendar（iPhone等）にワンタップで追加。予定の登録忘れを防ぎます。',
    },
  ],
  useCases: [
    '飲み会・食事会の日程調整',
    '社内ミーティングのスケジュール合わせ',
    '取引先との打ち合わせ日程の調整',
    'サークル・部活動のイベント日程決め',
  ],
  roadmap: '今後は繰り返しイベント対応、投票機能の追加、時間帯指定での調整機能などを予定しています。「日程調整はアワセルで」と言っていただけるサービスを目指してまいります。',
  quote: {
    text: '既存の日程調整サービスは広告が多かったり、UIが使いづらかったりと、シンプルなことをしたいだけなのにストレスを感じることがありました。「アワセル」は、日程調整に必要な機能だけを、とことんシンプルに提供します。',
    attribution: '株式会社Senrigan 代表取締役 宮川 清実',
  },
  cta: {
    heading: '「アワセル」を使ってみる',
    subtext: '登録不要・完全無料。今すぐ日程調整を始められます',
    primaryButton: { label: 'アワセルを開く', url: 'https://awaseru.net/' },
  },
};
